import { CustomerType } from "store/customers/customer-details/customer-details-types";
import { SecretExpirationStatus } from "store/environments/tenants/tenants-types";

export enum TenantMailerService {
    NotSet = 0,
    SmtpClient = 1,
    SendGrid = 2,
    Pepipost = 3,
    Postmark = 4,
    Office365Relay = 5,
}

export enum RecognitionPhase {
    Off = 0,
    First = 1,
    Last = 2,
}

export enum TenantFeatureGroup {
    automaticVendorAssigment = 0,
    mlWorkflow = 1,
    mlCompany = 2,
    mlCoding = 3,
    fromXml = 11,
    autoTtp = 4,
    poSettings = 5,
    vendor = 6,
    vendorSimilaritySearch = 12,
    sftp = 7,
    ocr = 8,
    search = 9,
    other = 10,
}

export interface Features {
    amazonInvoicesEnabled: boolean;
    autoTTPEnabled: boolean;
    autoTTPMaxBatchSize: number;
    axTurboSwitch: boolean;
    bankAccountComparisonLogicEnabled: boolean;
    codingDeliveryAddressEnabled: boolean;
    codingFromXMLEnabled: boolean;
    codingPredictionEnabled: boolean;
    codingPredictionImportMinScore: number;
    companyLogoUrl: string;
    companyPredictionEnabled: boolean;
    companyPredictionImportMinScore: number;
    contractsEnabled: boolean;
    currentPostingDate: boolean;
    d365RoleDooapRequiredToAccessDooap: boolean;
    dbaDefaultApprovers: boolean;
    dimensionDataLiveFiltering: boolean;
    dueDateFromPO: boolean;
    fillMissingProjectDimensionsFromVendorDefaults: boolean;
    invoiceEscalationEnabled: boolean;
    mailerService: number;
    ocrErrorReportingEnabled: boolean;
    poChargesGroupEnabled: boolean;
    poLinkEnabled: boolean;
    poPurchStatusInvoicedFilteringEnabled: boolean;
    positionHierarchyEnabled: boolean;
    poStatusFilteringEnabled: boolean;
    postingDateFromXmlEnabled: boolean;
    procurementLineEnabled: boolean;
    purchaseAgreementEnabled: boolean;
    recurringInvoicesEnabled: boolean;
    remitToAddressEnabled: boolean;
    salesTaxPredictionEnabled: boolean;
    searchEnabled: boolean;
    searchVendorBySearchName: boolean;
    sftpEnabled: boolean;
    ssrEnabled: boolean;
    studioManualAcceptanceEnabled: boolean;
    transferInvoiceDocumentsToAxDuringTransferToPayment: boolean;
    updateInvoiceWithPoData: boolean;
    useImportedSigningLimits: boolean;
    userSessionIdleTimeoutInMinutes: number;
    vatAmountValidation: boolean;
    vatRegistrationNumberValidationEnabled: boolean;
    vectorVendorRecognitionEnabled: boolean;
    vectorVendorRecognitionMinScore: number;
    vectorVendorRecognitionPhaseSelection: number;
    vendorPredictionEnabled: boolean;
    vendorPredictionVendorAddressMinScore: number;
    vendorPredictionVendorNameMinScore: number;
    vendorPrepaymentRegisterCheck: boolean;
    vendorSearchByAddress: boolean;
    waitForPrEnabled: boolean;
    workflowFromXml: boolean;
    workflowFromXmlFieldName: string;
    workflowPredictionEnabled: boolean;
    workflowPredictionImportMinScore: number;
}

export interface OcrConfiguration {
    ocrMethod: OcrMethods | null;
}

export enum OcrMethods {
    PhotonSync = 1,
    PhotonHitl = 2,
    Llm = 3,
    PassthroughOnly = 4,
}

export interface IErpInfo {
    appRegSecretExpiration: SecretExpiration;
    axSecretExpiration: SecretExpiration;
}

export interface SecretExpiration {
    timeInDays: number | null;
    status: SecretExpirationStatus | null;
    rotationDate: string | null;
}

export interface ITenantGeneralConfigurations {
    id: string;
    moniker: string;
    displayName: string;
    customerName: string | null;
    customerType: CustomerType;
    partners: string[];
    dooapMainUrl: string | null;
    additionalInformation: string | null;
    azureFunctionsBaseUrl: string | null;
    azureSearchApiBaseUrl: string | null;
}

export enum AuthenticationType {
    UsernamePassword = 0,
    ClientSecret,
}

export interface AuthenticationMethod {
    authenticationType: AuthenticationType;
    authenticationDescription: string;
}

export interface ErpCredentials {
    url: string;
    username: string;
    password?: string | null;
    clientId: string;
    clientSecret?: string | null;
    secretExpirationDate?: string | null;
}

export enum ThrottlingPriority {
    Low = 0,
    Medium = 1,
    High = 2,
}

export interface IErpThrottlingPriority {
    priority: ThrottlingPriority;
    priorityName: string;
}

export interface ICredentialsValidation {
    message: string;
    isValid: boolean;
}

export interface ITenantDefaultKeyVaultCompany {
    companyId: string;
}

export interface ITenantMaintenanceMode {
    maintenanceModeEnabled: boolean;
    maintenanceModeDooapDictionaryKey: string | null;
    maintenanceModeSystemDictionaryKey: string | null;
}

export interface IAllowedIpAddress {
    id: number | null;
    label: string;
    cidr: string;
    editing: boolean | null;
}

export interface ISftpUserWithIpAddresses {
    id: number;
    allowedIpAddresses: IAllowedIpAddress[];
}

export interface ISftpAccount {
    host: string;
    port: string;
    username: string;
    password: string;
}

export interface IIndexCounter {
    usage: number;
    quota: number;
}

export interface ISearchService {
    name: string;
    resourceGroup: string;
    subscription: string;
    indexCounter: IIndexCounter | null;
}
